import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { FontText, PrimaryButton, SEOHead } from '../components/common';
import { logErrorsWithMessage } from '../utils/errors';

export const Auth: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSignUp && password !== confirmPassword) {
      toast.error(t('passwords_dont_match'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('password_min_length'));
      return;
    }

    setLoading(true);

    try {
      const { error } = isSignUp
        ? await signUp(email, password)
        : await signIn(email, password);

      if (error) {
        if (isSignUp) {
          if (error.message === 'account_already_exists') {
            toast.error(t('account_already_exists'));
          } else if (error.message === 'invalid_email') {
            toast.error(t('invalid_email'));
          } else if (error.message === 'password_too_short') {
            toast.error(t('password_min_length'));
          } else if (
            error.message === 'update_failed' ||
            error.message === 'no_user_after_signup'
          ) {
            toast.error(t('sign_up_failed'));
          } else {
            logErrorsWithMessage(error, t('sign_up_failed'));
          }
        } else {
          logErrorsWithMessage(error, t('login_failed'));
        }
      } else {
        navigate('/');
      }
    } catch (error) {
      logErrorsWithMessage(error, t('authentication_failed'));
    } finally {
      setLoading(false);
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      padding: 20,
    },
    formContainer: {
      backgroundColor: theme.colors.surface,
      padding: 40,
      borderRadius: 20,
      width: '100%',
      maxWidth: '400px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    },
    title: {
      color: theme.colors.accent,
      textAlign: 'center',
      marginBottom: 10,
    },
    subtitle: {
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 30,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: 15,
    },
    input: {
      padding: '12px 16px',
      backgroundColor: theme.colors.overlay,
      border: `1px solid ${theme.colors.overlay}`,
      borderRadius: 8,
      color: theme.colors.text,
      outline: 'none',
      transition: 'border-color 0.3s',
    },
    passwordContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    passwordInput: {
      padding: '12px 16px',
      paddingRight: 48,
      backgroundColor: theme.colors.overlay,
      border: `1px solid ${theme.colors.overlay}`,
      borderRadius: 8,
      color: theme.colors.text,
      outline: 'none',
      transition: 'border-color 0.3s',
      width: '100%',
    },
    eyeButton: {
      position: 'absolute',
      right: 12,
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 4,
      color: theme.colors.textSecondary,
      fontSize: 18,
    },

    switchContainer: {
      textAlign: 'center',
      marginTop: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
    },
    switchText: {
      color: theme.colors.textSecondary,
    },
    switchButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    switchButtonText: {
      color: theme.colors.accent,
    },
  };

  return (
    <>
      <SEOHead
        title={isSignUp ? t('sign_up') : t('login')}
        description={
          isSignUp ? t('sign_up_description') : t('login_description')
        }
        canonical="/auth"
      />
      <div style={styles.container}>
        <div style={styles.formContainer}>
          <FontText h1 style={styles.title}>
            {t('app_name')}
          </FontText>
          <FontText h2 style={styles.subtitle}>
            {isSignUp ? t('sign_up') : t('login')}
          </FontText>

          <form onSubmit={handleSubmit} style={styles.form}>
            <input
              type="email"
              autoComplete="email"
              placeholder={t('email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={styles.input}
              required
            />

            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                placeholder={t('password')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.passwordInput}
                required
              />
              <button
                type="button"
                style={styles.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={
                  showPassword ? t('hide_password') : t('show_password')
                }
                tabIndex={-1}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>

            {isSignUp && (
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={t('confirm_password')}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  style={styles.passwordInput}
                  required
                />
                <button
                  type="button"
                  style={styles.eyeButton}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword
                      ? t('hide_password')
                      : t('show_password')
                  }
                  tabIndex={-1}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            )}

            <PrimaryButton
              type="submit"
              disabled={loading}
              loading={loading}
              style={{ marginTop: 10, borderRadius: 8 }}
            >
              {loading
                ? t('loading')
                : isSignUp
                  ? t('create_account')
                  : t('login')}
            </PrimaryButton>
          </form>

          <div style={styles.switchContainer}>
            <FontText small style={styles.switchText}>
              {isSignUp ? t('already_have_account') : t('dont_have_account')}
            </FontText>
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              style={styles.switchButton}
            >
              <FontText small style={styles.switchButtonText}>
                {isSignUp ? t('login') : t('sign_up')}
              </FontText>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
